/**
 * BrokerPicker — combobox courtier (parallèle à CompanyPicker).
 *
 * Valeur "(non assigné)" prise en charge — broker_id est nullable côté DB,
 * un projet peut exister sans courtier assigné (création par api_key MCP,
 * import en masse, etc.).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useBroker, useBrokersForPicker } from "../lib/queries/brokers";

type Props = {
  value: string | undefined;
  onChange: (brokerId: string | undefined) => void;
  id?: string;
  disabled?: boolean;
};

export function BrokerPicker({ value, onChange, id, disabled }: Props) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const selected = useBroker(value);

  const enabled = open && q.trim().length >= 2;
  // 9.BA.1 — `broker` ET `tenant_admin`.
  //
  // Le filtre par défaut ne retenait que `role='broker'`. Or dans un petit
  // cabinet, la personne qui monte les dossiers EST l'administrateur : au
  // premier parcours réel, la base ne contenait AUCUN utilisateur `broker`, et
  // le picker répondait « aucun courtier trouvé » quoi qu'on tape. Ce n'était
  // pas une recherche cassée — c'était un filtre qui excluait exactement les
  // gens qui existent.
  const results = useBrokersForPicker({ q: enabled ? q : undefined, role: "all" });

  useEffect(() => {
    if (!value) setQ("");
  }, [value]);

  if (value && selected.data) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-medium text-slate-900">
              {selected.data.full_name ?? selected.data.email}
            </div>
            <div className="text-xs text-slate-500">{selected.data.email}</div>
          </div>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => {
              onChange(undefined);
              setOpen(true);
            }}
            disabled={disabled}
          >
            {t("common.change")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-2">
        <input
          id={id}
          type="search"
          className="input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("brokers.search_placeholder")}
          disabled={disabled}
          autoComplete="off"
          aria-expanded={open}
        />
      </div>

      {open && enabled && (
        <div className="mt-1 max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          {results.isLoading && !results.data ? (
            <div className="p-3 text-sm text-slate-400">{t("app.loading")}</div>
          ) : results.data && results.data.items.length === 0 ? (
            // 9.BA.1 — un état vide qui INVITE, au lieu de se lire comme un
            // échec de recherche. « Aucun courtier trouvé » a fait conclure au
            // courtier pilote que la recherche était cassée ; elle était juste.
            <div className="p-3 text-sm text-slate-600">
              <p className="font-medium text-slate-800">{t("brokers.picker_empty_title")}</p>
              <p className="mt-0.5">{t("brokers.picker_empty_body")}</p>
              <Link
                to="/settings/team"
                className="text-brand-700 hover:text-brand-800 focus-visible:ring-brand-500 mt-2 inline-block rounded font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2"
              >
                {t("brokers.picker_empty_cta")}
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {results.data?.items.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left hover:bg-slate-50"
                    onClick={() => {
                      onChange(b.id);
                      setOpen(false);
                    }}
                  >
                    <div className="text-sm font-medium text-slate-900">
                      {b.full_name ?? b.email}
                    </div>
                    <div className="text-xs text-slate-500">{b.email}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {open && !enabled && (
        <p className="mt-1 text-xs text-slate-400">{t("common.search_min_chars", { count: 2 })}</p>
      )}

      <button
        type="button"
        onClick={() => onChange(undefined)}
        disabled={disabled || !value}
        className="mt-1 text-xs text-slate-500 hover:text-slate-700 disabled:opacity-30"
      >
        {t("brokers.unassign")}
      </button>
    </div>
  );
}
